import { Box, Button, Text } from '@elkaso-app/web-design';
import { PriceImportRun, useDownloadPriceImportTemplateApi } from 'apis/price-imports';

export interface MappingGuidanceProps {
  run: PriceImportRun;
}

/**
 * The mapping step, before automatic column reading exists.
 *
 * An unrecognised layout used to dead-end here with a one-line placeholder —
 * a run stuck at NEEDS MAPPING with nothing to click. Until inference ships,
 * the honest path is the template: download it, move the supplier's data in,
 * upload that. The template's layout is seeded as a global mapping profile, so
 * it reads with zero setup, and any layout mapped once is remembered for that
 * supplier afterwards.
 *
 * The detected columns themselves render in the MappingEditor below this
 * panel, which is also where a wrong-worksheet read becomes obvious.
 */
export const MappingGuidance = ({ run }: MappingGuidanceProps) => {
  const { refetch: downloadTemplate, isFetching } = useDownloadPriceImportTemplateApi();

  const isRecognised = run.mappingSource != null;

  if (isRecognised) {
    return (
      <Box maxW='680px'>
        <Text fontSize='sm' color='gray.700' mb='8px'>
          This file was read with{' '}
          {run.mappingSource === 'seeded'
            ? 'the Kaso template layout'
            : run.mappingSource === 'manual'
            ? 'a hand-made mapping'
            : 'a mapping saved from an earlier upload'}
          . Column-by-column review arrives with automatic reading.
        </Text>
        {run.sheetName && (
          <Text fontSize='xs' color='gray.500'>
            Worksheet: {run.sheetName}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Box maxW='680px'>
      <Text fontSize='sm' fontWeight='semibold' mb='6px'>
        This layout isn’t one we know yet
      </Text>
      <Text fontSize='sm' color='gray.700' mb='12px'>
        Automatic column reading hasn’t shipped, so a file only imports when its layout is already known. For now:
        download the Kaso template, move the supplier’s lines into it, and upload that file — it reads with no setup. A
        layout only needs to be understood once; after that, this supplier’s uploads in the same shape are recognised
        automatically.
      </Text>

      <Button size='sm' colorScheme='red' isLoading={isFetching} onClick={() => downloadTemplate()}>
        Download the template
      </Button>
    </Box>
  );
};
